import { PORTFOLIO_MANAGEMENT_V1_LICENSABLE_MODULES } from '../../modules/domain-bundles.js';
import { FEATURE_KEYS, MODULE_KEYS, OPERATION_IDS } from '../routes/ids.js';
const tk = (value) => value;
const fieldId = (value) => value;
const validationRule = (value) => value;
const commandId = (value) => value;
function required() {
    return {
        rules: [{ ruleId: validationRule('required'), messageKey: tk('validation.required') }],
        validateWhen: 'submit',
    };
}
function field(id, name, overrides = {}) {
    return {
        id: fieldId(id),
        name,
        labelKey: tk(id),
        fieldType: 'text',
        value: { hiddenValuePolicy: 'unregister', submitBehavior: 'include' },
        layout: { md: 6 },
        ...overrides,
    };
}
const moduleOptions = PORTFOLIO_MANAGEMENT_V1_LICENSABLE_MODULES.map((moduleId) => ({
    value: moduleId,
    labelKey: tk(`enum.module.${moduleId}`),
}));
const defaultLicenses = PORTFOLIO_MANAGEMENT_V1_LICENSABLE_MODULES.map((moduleId) => ({
    module: moduleId,
    maxBranches: 1,
}));
/** Platform owns commercial registration only; local bootstrap fields are absent. */
export const PLATFORM_ORGANIZATION_REGISTRATION_FORM = {
    id: 'form.admin.platformOrganization.create',
    schemaVersion: 1,
    module: MODULE_KEYS.ADMIN,
    feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
    privacyClassification: 'CONFIDENTIAL',
    telemetryId: 'experience.form.platform-organization-registration',
    accessibilityLabelKey: tk('a11y.form.platformOrganization.registration'),
    availability: 'AVAILABLE',
    owner: 'experience-registry',
    mode: 'create',
    titleKey: tk('form.platformOrganization.registrationTitle'),
    descriptionKey: tk('form.platformOrganization.registrationDescription'),
    fields: [
        field('field.organization.code', 'code', { validation: required() }),
        field('field.organization.name', 'name', { validation: required() }),
        field('field.organization.legalName', 'legalName', { validation: required() }),
        field('field.organization.jurisdiction', 'jurisdiction', {
            fieldType: 'hidden',
            validation: required(),
            value: { defaultValue: 'NP', hiddenValuePolicy: 'unregister', submitBehavior: 'include' },
        }),
        field('field.organization.locale', 'locale', {
            fieldType: 'select',
            validation: required(),
            options: [
                { value: 'en-US', labelKey: tk('locale.en-US') },
                { value: 'ne-NP', labelKey: tk('locale.ne-NP') },
            ],
            value: { defaultValue: 'en-US', hiddenValuePolicy: 'unregister', submitBehavior: 'include' },
        }),
        field('field.organization.timeZone', 'timeZone', {
            validation: required(),
            value: { defaultValue: 'Asia/Kathmandu', hiddenValuePolicy: 'unregister', submitBehavior: 'include' },
        }),
        field('field.organization.baseCurrency', 'baseCurrency', {
            fieldType: 'hidden',
            validation: required(),
            value: { defaultValue: 'NPR', hiddenValuePolicy: 'unregister', submitBehavior: 'include' },
        }),
        field('field.organization.panNumber', 'panNumber'),
        field('field.organization.sebonRegistrationNumber', 'sebonRegistrationNumber'),
        field('field.organization.licenses', 'licenses', {
            fieldType: 'collection',
            layout: { md: 12 },
            validation: required(),
            value: { defaultValue: defaultLicenses, hiddenValuePolicy: 'unregister', submitBehavior: 'include' },
            collection: {
                minItems: PORTFOLIO_MANAGEMENT_V1_LICENSABLE_MODULES.length,
                maxItems: PORTFOLIO_MANAGEMENT_V1_LICENSABLE_MODULES.length,
                addLabelKey: tk('platformOnboarding.license.add'),
                removeLabelKey: tk('platformOnboarding.license.remove'),
                emptyStateKey: tk('platformOnboarding.license.empty'),
                itemLabelKey: tk('platformOnboarding.license.item'),
                summaryMode: 'count',
                columns: [
                    { name: 'module', labelKey: tk('platformOnboarding.license.module'), fieldType: 'select', required: true, options: moduleOptions, layout: { md: 3 } },
                    { name: 'maxBranches', labelKey: tk('platformOnboarding.license.maxBranches'), fieldType: 'number', required: true, defaultValue: 1, layout: { md: 3 } },
                    { name: 'maxUsers', labelKey: tk('platformOnboarding.license.maxUsers'), fieldType: 'number', layout: { md: 3 } },
                    { name: 'effectiveFrom', labelKey: tk('platformOnboarding.license.effectiveFrom'), fieldType: 'date', layout: { md: 3 } },
                    { name: 'effectiveTo', labelKey: tk('platformOnboarding.license.effectiveTo'), fieldType: 'date', layout: { md: 3 } },
                    { name: 'graceEndsAt', labelKey: tk('platformOnboarding.license.graceEndsAt'), fieldType: 'date', layout: { md: 3 } },
                ],
            },
        }),
    ],
    sections: [
        {
            id: 'section.platformOrganization.identity',
            titleKey: tk('platformOnboarding.section.identity'),
            fields: ['field.organization.code', 'field.organization.name', 'field.organization.legalName', 'field.organization.jurisdiction', 'field.organization.locale', 'field.organization.timeZone', 'field.organization.panNumber', 'field.organization.sebonRegistrationNumber'].map(fieldId),
            layout: { columns: 2, gap: 16 },
        },
        {
            id: 'section.platformOrganization.licenses',
            titleKey: tk('platformOnboarding.section.licenses'),
            descriptionKey: tk('platformOnboarding.section.licensesDescription'),
            fields: [fieldId('field.organization.licenses')],
            layout: { columns: 1, gap: 16 },
        },
    ],
    actions: [
        {
            id: 'action.platformOrganization.cancel',
            schemaVersion: 1,
            module: MODULE_KEYS.ADMIN,
            feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
            privacyClassification: 'INTERNAL',
            telemetryId: 'experience.action.platform-organization-registration.cancel',
            accessibilityLabelKey: tk('a11y.action.cancel'),
            availability: 'AVAILABLE',
            owner: 'experience-registry',
            labelKey: tk('action.cancel'),
            iconKey: 'cancel',
            kind: 'localUi', submitBehavior: 'standalone', intent: 'secondary', idempotencyRequired: false,
            successBehavior: 'NO_OP', errorBehavior: 'SHOW_INLINE_ERROR', order: 10, group: 'secondary',
        },
        {
            id: 'action.platformOrganization.register',
            schemaVersion: 1,
            module: MODULE_KEYS.ADMIN,
            feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
            privacyClassification: 'CONFIDENTIAL',
            telemetryId: 'experience.action.platform-organization-registration.submit',
            accessibilityLabelKey: tk('a11y.action.platformOrganization.register'),
            availability: 'AVAILABLE',
            owner: 'experience-registry',
            operationId: OPERATION_IDS.PLATFORM_ORGANIZATIONS_CREATE,
            labelKey: tk('platformOnboarding.action.register'),
            iconKey: 'save',
            kind: 'command',
            submitBehavior: 'form_submit',
            submitConfig: { validatesForm: true, requiresDirty: true, requiresValid: true, resetBehavior: 'toServerValues' },
            commandId: commandId('cmd.admin.platform-organizations.create'),
            intent: 'primary', idempotencyRequired: true,
            successBehavior: 'NAVIGATE_BACK',
            successMessageKey: tk('platformOnboarding.registration.success'),
            errorBehavior: 'SHOW_INLINE_ERROR', errorMessageKey: tk('common.error.unexpected'), order: 20, group: 'primary',
        },
    ],
    validation: { validateWhen: 'submit' },
    submission: {
        defaultCommandId: commandId('cmd.admin.platform-organizations.create'),
        successMessageKey: tk('platformOnboarding.registration.success'),
        errorMessageKey: tk('platformOnboarding.registration.error'),
    },
    operationId: OPERATION_IDS.PLATFORM_ORGANIZATIONS_CREATE,
};
